import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast, Toaster } from "sonner";
import { AlertTriangle, Trash2, RefreshCw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Owner-only tool to reset test / seed dummy data so a fresh tester lands
 * on a clean slate. Never touches real usage rows — only rows whose fields
 * clearly say "TEST" or "iter*". Always shows a preview count first.
 */
export default function AdminWipe() {
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [wiped, setWiped] = useState(null);

  if (user && user.role !== "owner") {
    return (
      <div className="p-6 text-slate-700" data-testid="wipe-forbidden">
        This page is reserved for the highest-level account.
      </div>
    );
  }

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/wipe/preview");
      setPreview(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const runWipe = async () => {
    if (!preview) {
      toast.error("Click Preview first — the safety check needs the current count.");
      return;
    }
    const total = preview.total ?? Object.values(preview).reduce((a, b) => a + b, 0);
    if (!window.confirm(`Delete every clearly-tagged test row (${total} in total)? Real usage data is preserved.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/wipe/dummy?confirm_total=${total}`);
      setWiped(r.data);
      toast.success(`Deleted ${r.data.total_deleted} test rows ✓`);
      await runPreview();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wipe-root">
      <Toaster position="top-center" />
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Trash2 className="text-rose-600" size={28} /> Reset test data
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
          Removes only the seed / iteration rows the developer used while building — anything with
          &ldquo;TEST&rdquo; or &ldquo;iter&rdquo; in the name/notes/message. Real shift entries, real POs, real
          uploads and real attendance are kept.
        </p>
      </div>

      <Card className="p-4 border-2 border-amber-300 bg-amber-50 rounded-sm flex gap-3 items-start" data-testid="wipe-caution">
        <AlertTriangle className="text-amber-700 shrink-0" size={20} />
        <div className="text-sm text-amber-900">
          Always click <strong>Preview</strong> first and confirm the counts before deleting.
          Deletions cannot be undone (they&rsquo;re logged in the audit trail but the data itself is gone).
        </div>
      </Card>

      <div className="flex gap-2">
        <Button data-testid="wipe-preview-btn" onClick={runPreview} disabled={busy} className="bg-slate-900 hover:bg-slate-800 text-white">
          <RefreshCw size={14} className="mr-1" /> Preview
        </Button>
        <Button
          data-testid="wipe-run-btn"
          onClick={runWipe}
          disabled={busy || !preview}
          className="bg-rose-600 hover:bg-rose-700 text-white"
        >
          <Trash2 size={14} className="mr-1" /> Wipe now
        </Button>
      </div>

      {preview && (
        <Card className="p-4 border-2 border-slate-300 rounded-sm" data-testid="wipe-preview">
          <h3 className="font-bold text-slate-800 mb-2">Would delete</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
            {Object.entries(preview).map(([k, v]) => (
              <div key={k} className="p-2 border border-slate-300 rounded-sm bg-slate-50">
                <div className="text-xs text-slate-500 uppercase tracking-wider">{k.replace(/_/g, " ")}</div>
                <div className="font-bold text-lg text-slate-900">{v}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {wiped && (
        <Card className="p-4 border-2 border-emerald-400 bg-emerald-50 rounded-sm" data-testid="wipe-result">
          <div className="text-sm font-bold text-emerald-900 mb-2">
            Deleted {wiped.total_deleted} rows.
          </div>
          <pre className="text-xs bg-white p-2 border border-emerald-200 rounded-sm overflow-auto">
            {JSON.stringify(wiped.detail, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
}
